import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Autoplay from "embla-carousel-autoplay";
import ClassNames from "embla-carousel-class-names";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";
import Icon from "../../icons";
import { cn } from "../../utils";
import Button from "../Button";
import useTween from "./useTween";
const CarouselContext = React.createContext(null);
function useCarousel() {
    const context = React.useContext(CarouselContext);
    if (!context) {
        throw new Error("useCarousel must be used within a <Carousel />");
    }
    return context;
}
const Carousel = React.forwardRef(({ orientation = "horizontal", opts, setApi, className, children, autoplay = false, autoplayInterval = 2000, stopOnInteraction = false, stopOnMouseEnter = false, scale = false, ...props }, ref) => {
    const plugins = [
        autoplay &&
            Autoplay({
                delay: autoplayInterval,
                stopOnInteraction,
                stopOnMouseEnter,
            }),
        ClassNames(),
        // Add other plugins here, e.g.:
        // otherPlugin && OtherPlugin(options)
    ].filter(Boolean) || [];
    const [carouselRef, api] = useEmblaCarousel({
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
    }, plugins);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const { setTweenNodes, setTweenFactor, tweenScale } = useTween();
    const onSelect = useCallback((api) => {
        if (api) {
            setCanScrollPrev(api.canScrollPrev());
            setCanScrollNext(api.canScrollNext());
        }
    }, []);
    const onScroll = useCallback((api) => {
        if (api) {
            setScrollProgress(api.scrollProgress() * 100);
        }
    }, []);
    const scrollPrev = useCallback(() => {
        api?.scrollPrev();
    }, [api]);
    const scrollNext = useCallback(() => {
        api?.scrollNext();
    }, [api]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            scrollPrev();
        }
        else if (event.key === "ArrowRight") {
            event.preventDefault();
            scrollNext();
        }
    }, [scrollPrev, scrollNext]);
    useEffect(() => {
        if (!api || !setApi) {
            return;
        }
        setApi(api);
    }, [api, setApi]);
    useEffect(() => {
        if (api) {
            onSelect(api);
            api.on("reInit", onSelect);
            api.on("select", onSelect);
        }
        return () => {
            api?.off("select", onSelect);
        };
    }, [api, onSelect]);
    useEffect(() => {
        if (!api)
            return;
        onScroll(api);
        api.on("reInit", onScroll).on("scroll", onScroll).on("slideFocus", onScroll);
    }, [api, onScroll]);
    useEffect(() => {
        if (api && scale) {
            setTweenNodes(api);
            setTweenFactor(api);
            tweenScale(api);
            api.on("reInit", setTweenNodes).on("reInit", setTweenFactor).on("reInit", tweenScale).on("scroll", tweenScale).on("slideFocus", tweenScale);
        }
    }, [api, scale, tweenScale, setTweenFactor, setTweenNodes]);
    return (_jsx(CarouselContext.Provider, { value: {
            carouselRef,
            api,
            opts,
            orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
            scrollPrev,
            scrollNext,
            canScrollPrev,
            canScrollNext,
            scrollProgress,
        }, children: _jsx("div", { ref: ref, "data-testid": "carousel", onKeyDownCapture: handleKeyDown, className: cn("relative", className), role: "region", "aria-roledescription": "carousel", ...props, children: children }) }));
});
Carousel.displayName = "Carousel";
const CarouselContent = React.forwardRef(({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();
    return (_jsx("div", { ref: carouselRef, className: "overflow-hidden rounded-md", children: _jsx("div", { ref: ref, className: cn("flex", orientation === "horizontal" ? "-ml-1" : "-mt-1 flex-col", className), ...props }) }));
});
CarouselContent.displayName = "CarouselContent";
const CarouselItem = React.forwardRef(({ className, focus, onClick, ...props }, ref) => {
    const { orientation } = useCarousel();
    return (_jsx("div", { ref: ref, role: "group", "aria-roledescription": "slide", className: cn("relative min-w-0 shrink-0 grow-0 basis-full", focus &&
            "[&>*]:after:absolute [&>*]:after:top-0 [&>*]:after:left-0 [&>*]:after:h-full [&>*]:after:w-full [&>*]:after:rounded-md [&>*]:after:bg-black [&>*]:after:opacity-0", focus &&
            "[&:not(.is-snapped)>*]:after:opacity-70 [&:not(.is-snapped)>*]:after:transition-opacity [&:not(.is-snapped)>*]:after:duration-500 [&:not(.is-snapped)>*]:after:ease-in-out", orientation === "horizontal" ? "pl-2" : "pt-2", onClick && "[&.is-snapped>*]:cursor-pointer", className), onClick: onClick, ...props }));
});
CarouselItem.displayName = "CarouselItem";
const CarouselPrevious = React.forwardRef(({ className, variant = "outlined", size = "regular", ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();
    return (_jsxs(Button, { ref: ref, variant: variant, size: size, className: cn("size-8 rounded-full p-0", orientation === "vertical" && "rotate-90", className), disabled: !canScrollPrev, onClick: scrollPrev, ...props, children: [_jsx(Icon, { name: "arrow-left", className: "shrink-0" }), _jsx("span", { className: "sr-only", children: "Previous slide" })] }));
});
CarouselPrevious.displayName = "CarouselPrevious";
const CarouselNext = React.forwardRef(({ className, variant = "outlined", size = "regular", ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();
    return (_jsxs(Button, { ref: ref, variant: variant, size: size, className: cn("size-8 rounded-full p-0", orientation === "vertical" && "rotate-90", className), disabled: !canScrollNext, onClick: scrollNext, ...props, children: [_jsx(Icon, { name: "arrow-right", className: "shrink-0" }), _jsx("span", { className: "sr-only", children: "Next slide" })] }));
});
CarouselNext.displayName = "CarouselNext";
const CarouselProgressBar = () => {
    const { scrollProgress } = useCarousel();
    return (_jsx("div", { "data-testid": "carousel-progress-bar", className: "relative h-2 w-1/2 max-w-40 overflow-hidden rounded-full border border-gray", children: _jsx("div", { className: "-left-full absolute top-0 bottom-0 w-full bg-gray-10", style: {
                transform: `translate3d(${scrollProgress}%, 0px, 0px)`,
            } }) }));
};
export { CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, CarouselProgressBar, useCarousel };
export default Carousel;
